import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import useVerticals from 'data_sources/useVerticals';
import {
  updateAccreditationVersion,
  destroyAccreditationVersion,
} from '../../../_data_sources/AccreditationVersions/manageAccreditationVersions';
import useAccreditingBodyVersion from '../../../_data_sources/AccreditationVersions/useAccreditationVersion';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { AccreditationBody, AccreditationVersion } from '../../../_types/awb';
import Loading from 'views/App/Portal/Loading';
import { Delete } from '@mui/icons-material';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import VerticalPicker from 'components/_pickers/VerticalPicker';

export type EditAccreditingBodyVersionProps = {
  accreditingBody: AccreditationBody;
  id: string;
  setLastId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditId: React.Dispatch<React.SetStateAction<string | null>>;
};

const EditAccreditingBodyVersion: React.FC<EditAccreditingBodyVersionProps> = ({
  accreditingBody,
  id,
  setEditId,
  setLastId,
}: EditAccreditingBodyVersionProps) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const verticalsQuery = useVerticals();

  const versionQuery = useAccreditingBodyVersion(accreditingBody.id!, id);

  const accreditingBodychema = Yup.object().shape({
    version: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodyVersions.properties.version')} is required`),
    abbreviation: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodyVersions.properties.abbreviation')} is required`),
    jurisdictions: Yup.array()
      .transform((value, originalValue) => {
        return originalValue.filter((x: string) => x.length > 0);
      })
      .required('At least one jurisdiction is required'),
    verticalIds: Yup.array()
      .of(Yup.string().required())
      .min(1, `${t('awb.accreditingBodyVersions.properties.vertical')} is required`),
    status: Yup.string().oneOf(['ENABLED', 'DISABLED']).optional().default('ENABLED'),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      version: '',
      abbreviation: '',
      jurisdictions: [],
      verticalIds: [] as string[],
      status: 'ENABLED' as const,
    },
    resolver: yupResolver(accreditingBodychema),
  });

  useEffect(() => {
    if (versionQuery.data?.version) {
      const version = versionQuery.data;
      const verticalIds = version.verticals?.length
        ? version.verticals
        : version.verticalId
          ? [version.verticalId]
          : [];
      reset({
        ...version,
        jurisdictions: version.jurisdictions?.map((x) => (typeof x === 'string' ? x : (x as { id: string }).id)) ?? [],
        verticalIds,
        status: version.status ?? 'ENABLED',
      });
    }
  }, [versionQuery.data, id, reset]);

  const handleSave: SubmitHandler<AccreditationVersion & { verticalIds?: string[] }> = (values) => {
    setIsSaving(true);
    const dataToSend: AccreditationVersion = {
      ...values,
      verticals: values.verticalIds ?? [],
      status: values.status,
    };
    updateAccreditationVersion(
      accreditingBody.id!,
      id,
      dataToSend,
      (data) => {
        reset();
        setServerErrors(undefined);
        queryClient.invalidateQueries(['accreditation_versions', accreditingBody.id]);
        queryClient.invalidateQueries(['accreditation_version', id]);
        if (data.id) setLastId(data.id);
        setEditId(null);

        setIsSaving(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  const handleDelete = () => {
    confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('common.delete'),
      description: t('awb.accreditingBodyVersions.delete.body'),
    })
      .then(async () => {
        setIsDeleting(true);
        await destroyAccreditationVersion(
          accreditingBody.id!,
          id,
          () => {
            reset();
            queryClient.invalidateQueries(['accreditation_versions']);
            queryClient.invalidateQueries(['accreditation_version', id]);
            setLastId(null);
            setEditId(null);
            enqueueSnackbar(parse(t(`awb.accreditingBodyVersions.delete.success`)), {
              variant: 'success',
              autoHideDuration: 6000,
            });

            setIsDeleting(false);
          },
          (errors: ServerErrorResponse) => {
            setIsDeleting(false);
            setServerErrors(errors);
          }
        );
      })
      .catch(() => {
        setIsDeleting(false);
        // don't do it
      });
  };

  return (
    <>
      <Modal
        title={t('awb.accreditingBodyVersions.edit.title')}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box>
              <Button
                className='icon-only'
                color='error'
                onClick={() => handleDelete()}
              >
                <Delete />
              </Button>
            </Box>
            {!isDeleting && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  color='neutral'
                  onClick={() => setEditId(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={() => {
                    trigger();
                  }}
                  disabled={isSaving}
                  loading={isSaving}
                  loadingPosition='end'
                  component='button'
                  type='submit'
                  variant='contained'
                >
                  {t('common.save')}
                </Button>
              </Box>
            )}
          </Box>
        }
        open
        onClose={() => setEditId(null)}
      >
        <>
          {versionQuery.isLoading || versionQuery.isFetching ? (
            <Loading />
          ) : (
            <>
              {isDeleting ? (
                <Alert
                  severity='warning'
                  icon={<CircularProgress size={20} />}
                >
                  {t('awb.accreditingBodyVersions.edit.deleting')}
                </Alert>
              ) : (
                <>
                  <Alert severity='warning'>{t('awb.accreditingBodyVersions.edit.warning')}</Alert>
                  {errorHelper.renderAllErrors(serverErrors, errors)}
                  <TextField
                    id='version'
                    key='version'
                    label={t('awb.accreditingBodyVersions.properties.version')}
                    variant='outlined'
                    required
                    fullWidth
                    {...register('version')}
                    error={!!errors.version}
                    helperText={errors.version ? errors.version.message : undefined}
                  />
                  <TextField
                    id='abbreviation'
                    key='abbreviation'
                    label={t('awb.accreditingBodyVersions.properties.abbreviation')}
                    variant='outlined'
                    required
                    fullWidth
                    {...register('abbreviation')}
                    error={!!errors.abbreviation}
                    helperText={errors.abbreviation ? errors.abbreviation.message : undefined}
                  />
                  <Controller
                    name='jurisdictions'
                    control={control}
                    render={({ field }) => (
                      <JurisdictionPicker
                        allowMultiple
                        id='jurisdictions'
                        {...field}
                        label={t('awb.accreditingBodyVersions.properties.jurisdictions')}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        required
                        disabled
                        error={!!errors.jurisdictions}
                        helperText={errors.jurisdictions ? errors.jurisdictions.message : undefined}
                      />
                    )}
                  />
                  <Controller
                    name='verticalIds'
                    control={control}
                    render={({ field }) => (
                      <VerticalPicker
                        id='verticalIds'
                        {...field}
                        label={t('awb.accreditingBodyVersions.properties.vertical')}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        allowMultiple
                        required
                        disabled
                        error={!!errors.verticalIds}
                        helperText={errors.verticalIds ? errors.verticalIds.message : undefined}
                      />
                    )}
                  />
                </>
              )}
            </>
          )}
        </>
      </Modal>
    </>
  );
};

export default EditAccreditingBodyVersion;
